import { useState, useCallback } from 'react';
import { dbManagerService, type DbQueryRequest, type DbModelsResponse } from '@/services/dbManager.service';

export function useDbManager() {
  const [data, setData] = useState<unknown[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [metadata, setMetadata] = useState<DbModelsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginación Principal
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const fetchModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await dbManagerService.listarModelos();
      if (res.success) {
        setModels(res.models.map((m) => m.name));
        setMetadata(res);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const execute = useCallback(
    async (query: DbQueryRequest, overridePage?: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const pageToUse = overridePage ?? currentPage;

        // Inyectar paginación si es findMany
        if (query.action === 'findMany') {
          query.args = {
            ...query.args,
            take: pageSize,
            skip: (pageToUse - 1) * pageSize,
          };
        }

        const response = await dbManagerService.executeQuery(query);

        if (response.success) {
          if (query.action === 'findMany') {
            setData(Array.isArray(response.data) ? response.data : []);
            if (response.total !== undefined) setTotalCount(response.total);
          }
          return response;
        } else {
          setError(response.error || 'Error en la consulta');
          return response;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error de conexión';
        setError(message);
        return { success: false, data: [], error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  return {
    data,
    models,
    metadata,
    isLoading,
    error,
    totalCount,
    currentPage,
    pageSize,
    setCurrentPage,
    execute,
    fetchModels,
    setData,
  };
}
